import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog, PaginationControl } from '@repo/shared-client/components/blocks'
import {
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Separator,
} from '@repo/shared-client/components/ui'
import { useNavigate } from '@tanstack/react-router'
import { ProfileInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, SwatchBookIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileBanner from 'src/components/primatives/sf-profile-banner/sf-profile-banner'
import type {
  ApprovePaymentRequestMutation,
  ApprovePaymentRequestMutationVariables,
  DeletePaymentRequestMutation,
  DeletePaymentRequestMutationVariables,
  PaymentRequestsListQuery,
  PaymentRequestsListQueryVariables,
} from 'types/graphql'

dayjs.extend(relativeTime)

export const PAYMENT_REQUESTS_LIST_QUERY: TypedDocumentNode<
  PaymentRequestsListQuery,
  PaymentRequestsListQueryVariables
> = gql`
  query PaymentRequestsListQuery($page: Int, $filter: PaymentRequestFilter, $sortBy: PaymentRequestSortEnum) {
    paymentRequestsListPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        createdAt
        updatedAt
        profileId
        days
        partnerIds
        partnerDisplayNames
        total
        profile {
          ...ProfileInfo
          uniqueId
          email
        }
      }
      count
      perPage
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`
export const DELETE_PAYMENT_REQUEST: TypedDocumentNode<
  DeletePaymentRequestMutation,
  DeletePaymentRequestMutationVariables
> = gql`
  mutation DeletePaymentRequest($id: String!) {
    deletePaymentRequest(id: $id) {
      id
    }
  }
`

export const APPROVE_PAYMENT_REQUEST: TypedDocumentNode<
  ApprovePaymentRequestMutation,
  ApprovePaymentRequestMutationVariables
> = gql`
  mutation ApprovePaymentRequest($id: String!) {
    approvePaymentRequest(id: $id) {
      id
      createdAt
      updatedAt
      profileId
      days
      partnerIds
      total
      refunded
    }
  }
`

type PaymentRequest = NonNullable<NonNullable<PaymentRequestsListQuery['paymentRequestsListPage']>['entries'][0]>

interface PaymentRequestCardProps {
  paymentRequest: PaymentRequest
  onApproved: () => void
  onDeleted: () => void
}

function PaymentRequestCard({ paymentRequest, onApproved, onDeleted }: PaymentRequestCardProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [approveModal, setApproveModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [showApproveSpinner, setShowApproveSpinner] = useState(false)
  const [showDeleteSpinner, setShowDeleteSpinner] = useState(false)
  const navigate = useNavigate()

  const [approvePaymentRequest] = useMutation(APPROVE_PAYMENT_REQUEST, {
    onCompleted: () => {
      setShowApproveSpinner(false)
      setApproveModal(false)
      toast.success(t('Payment request approved'))
      onApproved()
    },
    onError: (err) => {
      setShowApproveSpinner(false)
      toast.error(err.message)
    },
  })

  const [deletePaymentRequest] = useMutation(DELETE_PAYMENT_REQUEST, {
    onCompleted: () => {
      setShowDeleteSpinner(false)
      setDeleteModal(false)
      toast.success(t('Payment request deleted'))
      onDeleted()
    },
    onError: (err) => {
      setShowDeleteSpinner(false)
      toast.error(err.message)
    },
  })

  const handleApproveConfirm = () => {
    setShowApproveSpinner(true)
    approvePaymentRequest({ variables: { id: paymentRequest.id } })
  }

  const handleDeleteConfirm = () => {
    setShowDeleteSpinner(true)
    deletePaymentRequest({ variables: { id: paymentRequest.id } })
  }

  return (
    <>
      <Card className="w-full overflow-hidden py-0 shadow-sm">
        <Collapsible onOpenChange={setExpanded} open={expanded}>
          <CardContent className="flex flex-col gap-0 p-0">
            <div className="flex min-h-10 w-full items-stretch">
              <CollapsibleTrigger
                render={
                  <Button
                    aria-expanded={expanded}
                    className="h-auto min-h-10 w-full flex-1 justify-start rounded-none px-3 py-2 data-[state=open]:bg-muted/60"
                    type="button"
                    variant="ghost"
                  />
                }
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <div className="min-w-0 flex-1 overflow-visible">
                    <SFProfileBanner
                      disableLinks
                      lowerTitle={t('Created - {{time}}', { time: dayjs(paymentRequest.createdAt).fromNow() })}
                      profile={paymentRequest.profile}
                      size="md"
                    />
                  </div>
                  <div className="flex flex-col items-center gap-0">
                    <span className="text-base">{paymentRequest.profile?.uniqueId}</span>
                    <span className="text-base text-muted-foreground">${paymentRequest.total.toFixed(2)}</span>
                  </div>
                  {expanded ? (
                    <ChevronUpIcon className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
                  )}
                </div>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="border-border border-t px-4 py-3">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Email')}</span>
                    <span className="text-end">{paymentRequest.profile?.email ?? '—'}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Unique ID')}</span>
                    <span className="font-mono">{paymentRequest.profile?.uniqueId ?? '—'}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Days')}</span>
                    <span className="font-medium">{paymentRequest.days}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Total')}</span>
                    <span className="font-medium">${paymentRequest.total.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Partners')}</span>
                    {paymentRequest.partnerDisplayNames?.length ? (
                      <ul className="list-inside list-disc ps-4">
                        {paymentRequest.partnerDisplayNames.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-base">—</span>
                    )}
                  </div>

                  <Separator className="mt-2" />
                  <div className="flex w-full shrink-0 items-center justify-between">
                    <Button
                      aria-label={t('Delete payment request')}
                      onClick={() => setDeleteModal(true)}
                      type="button"
                      variant="ghost"
                    >
                      <TrashIcon />
                    </Button>
                    <div className="flex gap-0.5">
                      <Button
                        aria-label={t('Approve payment')}
                        onClick={() => setApproveModal(true)}
                        type="button"
                        variant="ghost"
                      >
                        <CheckIcon className="text-green-600" />
                      </Button>
                      <Button
                        aria-label={t('Edit payment request')}
                        onClick={() =>
                          navigate({
                            to: '/admin/payments/requests/$paymentRequestId/edit',
                            params: { paymentRequestId: paymentRequest.id },
                          } as never)
                        }
                        type="button"
                        variant="ghost"
                      >
                        <EditIcon className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </CardContent>
        </Collapsible>
      </Card>
      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Approve')}
        loading={showApproveSpinner}
        onConfirm={handleApproveConfirm}
        onOpenChange={(e) => setApproveModal(e.open)}
        open={approveModal}
        text={t(
          'Are you sure you want to approve this payment request? This will process the payment and grant supporter status.'
        )}
        title={t('Approve Payment Request')}
        variant="basic"
      />
      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Delete')}
        loading={showDeleteSpinner}
        onConfirm={handleDeleteConfirm}
        onOpenChange={(e) => setDeleteModal(e.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this payment request? This action cannot be undone.')}
        title={t('Delete Payment Request')}
        variant="delete"
      />
    </>
  )
}

type AdminPaymentRequestsProps = {
  page: number
  paymentRequestsListPage: PaymentRequestsListQuery['paymentRequestsListPage'] | undefined
  refetch: () => Promise<unknown>
}

const AdminPaymentRequests = (props: AdminPaymentRequestsProps) => {
  const { t } = useTranslation()
  const currentPage = props.page
  const paymentRequests = props.paymentRequestsListPage
  const entries = paymentRequests?.entries ?? []

  return (
    <div className="flex w-full flex-col gap-2">
      {entries.length > 0 ? (
        <div className="flex w-full flex-col gap-2">
          {entries.map((pr) => (
            <PaymentRequestCard
              key={pr.id}
              onApproved={() => {
                props.refetch()
              }}
              onDeleted={() => {
                props.refetch()
              }}
              paymentRequest={pr}
            />
          ))}
        </div>
      ) : (
        <Empty className="border-border border-dashed">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SwatchBookIcon />
            </EmptyMedia>
            <EmptyTitle>{t('No payment requests found')}</EmptyTitle>
            <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      {paymentRequests && paymentRequests.count > 0 ? (
        <PaginationControl count={paymentRequests.count} page={currentPage} pageSize={paymentRequests.perPage ?? 25} />
      ) : null}
    </div>
  )
}

export default AdminPaymentRequests
